import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { Modal } from "../../components/Modal";
import { Paragraph } from "../../components/Paragraph";
import { Slider } from "../../components/Slider";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import { useIndividual } from "../../data/useIndividual";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import { useMandateCheckList } from "../../data/useMandateCheckList";
import { ROUTE } from "../../routes";
import {
  screeningWizardCheckNames,
  selectionCoversAdverseMedia,
  selectionCoversSanctions,
} from "../../utils/screeningChecks";
import {
  CheckContainer,
  CheckTable,
  CheckTableCell,
  CheckTableHeaderRow,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import { ApprovalComment } from "./approvalComment";
import { CheckFields } from "./CheckFields";
import { CheckResults } from "./CheckResults";
import { IndividualChecksMap } from "./helper";
import { ManualAcceptReject } from "./ManualAcceptReject";

type PreviousName = {
  id?: number | string;
  name: string;
  ceasedOn?: string | null;
  reason?: string | null;
  fieldResultId?: number;
};

const CompactTableCell = styled(CheckTableCell)`
  ${({ theme: { colors, space } }: any) => `
    padding: ${space[2]};
    border-bottom: 1px solid ${colors.gray[10]};
    color: ${colors.gray[60]};
    vertical-align: middle;
  `}
`;

const CheckboxLabel = styled.label`
  ${({ theme: { colors, fontFamily, fontSizes, space } }: any) => `
    align-items: center;
    color: ${colors.blue[100]};
    display: flex;
    font-family: ${fontFamily};
    font-size: ${fontSizes[1]};
    gap: ${space[1]};
    margin-bottom: ${space[2]};
  `}
`;

const displayValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "-";
  return String(value);
};

const displayDate = (value?: string | null) => {
  if (!value) return "-";
  const date = DateTime.fromISO(value);
  return date.isValid ? date.toFormat("dd/MM/yyyy") : value;
};

const getNameFromParts = (row: any) =>
  [row?.first_name, row?.middle_name, row?.last_name].filter(Boolean).join(" ");

const toPreviousName = (row: any, index: number): PreviousName | null => {
  const name =
    row?.name ??
    row?.previous_name ??
    row?.entity_name ??
    row?.company_name ??
    getNameFromParts(row);

  if (!name) return null;

  return {
    id: row?.id ?? index,
    name,
    ceasedOn:
      row?.ceased_on ??
      row?.change_date ??
      row?.changed_at ??
      row?.date ??
      null,
    reason: row?.reason ?? null,
    fieldResultId: row?.field_result_id ?? row?.checkfield_result_id,
  };
};

const getInstancePreviousNames = (instance: any) => {
  const rows = instance?.previous_names ?? [];

  return Array.isArray(rows) ? rows : [];
};

const getFieldResultPreviousNames = (fieldResults: any[] = []) =>
  fieldResults
    .map((fieldResult, index) => {
      const row = toPreviousName(fieldResult, index);
      if (!row && fieldResult?.result) {
        return {
          id: fieldResult.id ?? index,
          name: fieldResult.result,
          fieldResultId: fieldResult.id,
        };
      }

      return row
        ? {
            ...row,
            fieldResultId: row.fieldResultId ?? fieldResult.id,
          }
        : null;
    })
    .filter(Boolean) as PreviousName[];

const getPreviousNames = (instance: any, individual: any, check: any) => {
  if (Array.isArray(instance?.previous_names)) {
    return instance.previous_names
      .map(toPreviousName)
      .filter(Boolean) as PreviousName[];
  }

  const instanceNames = getInstancePreviousNames(instance)
    .map(toPreviousName)
    .filter(Boolean) as PreviousName[];

  if (instanceNames.length > 0) return instanceNames;

  const individualNames = (individual?.former_name ?? [])
    .map(toPreviousName)
    .filter(Boolean) as PreviousName[];

  if (individualNames.length > 0) return individualNames;

  return getFieldResultPreviousNames(check?.individual_checkfield_result);
};

export const PreviousNamesCheck = (props: any) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;
  const params = useParams();
  const navigate = useNavigate();

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);
  const { data: individualData } = useIndividual(params.id);
  const { data: mandateChecksData, isLoading: isLoadingMandateChecks } =
    useMandateCheckList(false, screeningWizardCheckNames("entity"));

  const check = individualCheckData?.data;
  const instance: any = check?.instance;
  const individual = individualData?.data;
  const profile = individual?.profile;
  const title =
    check?.title || IndividualChecksMap.entity_previous_name_check.title;

  const [selectedName, setSelectedName] = useState<PreviousName | null>(null);
  const [selectedChecks, setSelectedChecks] = useState<string[]>([]);

  const previousNames = useMemo(
    () => getPreviousNames(instance, individual, check),
    [instance, individual, check],
  );

  const availableChecks = mandateChecksData?.data ?? [];

  const toggleCheck = (name: string) => {
    setSelectedChecks((prev) =>
      prev.includes(name)
        ? prev.filter((selected) => selected !== name)
        : [...prev, name],
    );
  };

  const closeModal = () => {
    setSelectedName(null);
    setSelectedChecks([]);
  };

  const createScreening = () => {
    if (!selectedName || selectedChecks.length === 0) return;

    const selectedCheckObjects = availableChecks.filter((checkOption: any) =>
      selectedChecks.includes(checkOption.name),
    );

    if (!selectedCheckObjects.length) return;

    const profileId = profile?.id;
    if (!profileId) return;

    navigate(
      ROUTE.PROFILE_SCREENING_ADD_PERSON.replace(":id", profileId.toString()),
      {
        state: {
          checkpointIds: selectedCheckObjects.map(
            (checkOption: any) => checkOption.id,
          ),
          selectedChecks: selectedCheckObjects,
          isAdverseMediaSelected: selectionCoversAdverseMedia(selectedChecks),
          isSanctionsSelected: selectionCoversSanctions(selectedChecks),
          showAddressStep: false,
          previousNameCheckId: id,
          previousNameFieldResultId: selectedName.fieldResultId,
          previousNameId: selectedName.id,
          screeningSubjectName: selectedName.name,
          prefillEntityDetails: {
            name: selectedName.name,
            country: individual?.country?.id ?? profile?.country?.id ?? null,
            entity_type:
              individual?.entity_type?.id ?? profile?.entity_type?.id ?? null,
            company_status:
              individual?.company_status?.id ??
              profile?.company_status?.id ??
              profile?.company_status ??
              null,
            registration_number:
              individual?.registration_number ||
              profile?.registration_number ||
              "",
            date_of_incorporation:
              individual?.date_of_incorporation ||
              profile?.date_of_incorporation ||
              null,
          },
        },
      },
    );
  };

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <CheckTitle>{title}</CheckTitle>

        {check?.status?.status === CheckStatusEnum.Review && (
          <ManualAcceptReject id={id} title={title} isMonitoring={false} />
        )}
      </Flex>

      {check?.status?.status && <CheckResults status={check.status.status} />}

      {instance?.approval_comment && (
        <ApprovalComment
          data={instance.approval_comment}
          status={check?.status?.status}
        />
      )}

      <CheckFields
        individual_checkfield_result={check?.individual_checkfield_result}
      />

      <Section pb={3}>
        <H3 mb={2}>Previous names</H3>

        {previousNames.length === 0 ? (
          <Paragraph color="gray.60" fontSize={1}>
            No previous names declared.
          </Paragraph>
        ) : (
          <CheckTable>
            <thead>
              <CheckTableHeaderRow>
                <CheckTableCell>Name</CheckTableCell>
                <CheckTableCell>Ceased on</CheckTableCell>
                <CheckTableCell>Reason</CheckTableCell>
                <CheckTableCell></CheckTableCell>
              </CheckTableHeaderRow>
            </thead>
            <tbody>
              {previousNames.map((previousName, index) => (
                <tr key={`${previousName.id ?? previousName.name}-${index}`}>
                  <CompactTableCell>
                    <Paragraph fontSize={1} fontWeight={600} my={0}>
                      {displayValue(previousName.name)}
                    </Paragraph>
                  </CompactTableCell>
                  <CompactTableCell>
                    {displayDate(previousName.ceasedOn)}
                  </CompactTableCell>
                  <CompactTableCell>
                    {displayValue(previousName.reason)}
                  </CompactTableCell>
                  <CompactTableCell>
                    <Flex justifyContent="flex-end">
                      <Button
                        size="regular"
                        variant="gray"
                        onClick={() => {
                          setSelectedName(previousName);
                          setSelectedChecks([]);
                        }}
                      >
                        Create Screening
                      </Button>
                    </Flex>
                  </CompactTableCell>
                </tr>
              ))}
            </tbody>
          </CheckTable>
        )}
      </Section>

      <Modal
        isOpen={Boolean(selectedName)}
        onClose={closeModal}
        width="500px"
        hasPrimaryButton={false}
      >
        <Box>
          <H3>Create a screening</H3>
          <Paragraph color="gray.60" fontSize={1} mb={3}>
            Select the check(s) to run against {selectedName?.name}.
          </Paragraph>

          {isLoadingMandateChecks ? (
            <Flex justifyContent="center" alignItems="center" minHeight="15vh">
              <Loader />
            </Flex>
          ) : (
            availableChecks.map((checkOption: any) => (
              <CheckboxLabel key={checkOption.id}>
                <input
                  type="checkbox"
                  checked={selectedChecks.includes(checkOption.name)}
                  onChange={() => toggleCheck(checkOption.name)}
                />
                {checkOption.actual_name}
              </CheckboxLabel>
            ))
          )}

          <Flex justifyContent="flex-end" mt={3}>
            <Button type="button" variant="text" mr={2} onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={createScreening}
              isDisabled={selectedChecks.length === 0 || isLoadingMandateChecks}
            >
              Create Screening
            </Button>
          </Flex>
        </Box>
      </Modal>
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
